import {
    type FC,
    useContext,
    createContext,
    useCallback,
    useMemo,
    useRef,
    useState,
    type ReactNode,
    type RefObject,
} from 'react';
import scrollIntoView from 'scroll-into-view';

type PdpSharedSimilarSoldItemsContextType = {
    onPdpMobileEstimateValueBadgeClick: () => void;
    onPdpRespEstimateValueBadgeClick: () => void;
    setIsSimilarSoldItemsOpen: (open: boolean) => void;
    isSimilarSoldItemsOpen: boolean;
    similarSoldItemsSectionRef: RefObject<HTMLDivElement | null>;
};

const PdpSharedSimilarSoldItemsContext = createContext<PdpSharedSimilarSoldItemsContextType | null>(
    null
);

export const PdpSharedSimilarSoldItemsContextProvider: FC<{
    children: ReactNode;
}> = ({ children }) => {
    const [isSimilarSoldItemsOpen, setIsSimilarSoldItemsOpen] = useState<boolean>(false);
    const similarSoldItemsSectionRef = useRef<HTMLDivElement | null>(null);

    const onPdpMobileEstimateValueBadgeClick = useCallback(() => {
        setIsSimilarSoldItemsOpen(true);
    }, []);

    const onPdpRespEstimateValueBadgeClick = useCallback(() => {
        const el = similarSoldItemsSectionRef?.current;
        if (el) {
            scrollIntoView(el, { time: 500, align: { top: 0.4 } });
            setIsSimilarSoldItemsOpen(true);
        }
    }, []);

    const value = useMemo(
        () => ({
            onPdpMobileEstimateValueBadgeClick,
            onPdpRespEstimateValueBadgeClick,
            similarSoldItemsSectionRef,
            setIsSimilarSoldItemsOpen,
            isSimilarSoldItemsOpen,
        }),
        [
            onPdpMobileEstimateValueBadgeClick,
            onPdpRespEstimateValueBadgeClick,
            setIsSimilarSoldItemsOpen,
            isSimilarSoldItemsOpen,
        ]
    );

    return (
        <PdpSharedSimilarSoldItemsContext.Provider value={value}>
            {children}
        </PdpSharedSimilarSoldItemsContext.Provider>
    );
};

export const usePdpSharedSimilarSoldItemsContext = (): PdpSharedSimilarSoldItemsContextType => {
    const context = useContext(PdpSharedSimilarSoldItemsContext);
    if (!context) {
        throw new Error(
            'usePdpSharedSimilarSoldItemsContext must be used within PdpSharedSimilarSoldItemsContextProvider'
        );
    }
    return context;
};
